import { useState, useEffect, useCallback, useMemo } from 'react';
import { localToday, calendarDate, splitAmount, debtLabel } from '../lib/expense-utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Wallet, Receipt, ArrowRightLeft, Plus, Pencil, Trash2, LogOut, TrendingUp, TrendingDown, Download } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ token, onLogout }) => {
  const [expenses, setExpenses] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settlingUp, setSettlingUp] = useState(false);
  const [reportMonth, setReportMonth] = useState(localToday().slice(0, 7));
  const [exporting, setExporting] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  
  // Form state
  const [expenseName, setExpenseName] = useState('');
  const [cost, setCost] = useState('');
  const [whoPaid, setWhoPaid] = useState('Chris');
  const amountOtherOwes = splitAmount(cost);
  const [isCredit, setIsCredit] = useState(false);
  const [expenseDate, setExpenseDate] = useState(localToday());
  const [submitting, setSubmitting] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCost, setEditCost] = useState('');
  const [editWhoPaid, setEditWhoPaid] = useState('Chris');
  const editAmountOwes = splitAmount(editCost, editingExpense?.is_settlement);
  const [editIsCredit, setEditIsCredit] = useState(false);
  const [editDate, setEditDate] = useState('');

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [expensesRes, balanceRes] = await Promise.all([
        axios.get(`${API}/expenses`, { headers: authHeaders }),
        axios.get(`${API}/balance`, { headers: authHeaders })
      ]);
      setExpenses(expensesRes.data);
      setBalance(balanceRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    if (!/^\d{4}-\d{2}$/.test(reportMonth)) {
      toast.error('Please choose a month');
      return;
    }
    setExporting(true);
    try {
      const response = await axios.get(`${API}/reports/monthly`, {
        headers: authHeaders, params: { month: reportMonth }, responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expense-report-${reportMonth}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Monthly report downloaded');
    } catch {
      toast.error('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!expenseName.trim()) {
      toast.error('Please enter an expense name');
      return;
    }
    if (!cost || parseFloat(cost) <= 0) {
      toast.error('Please enter a valid cost');
      return;
    }
    if (!amountOtherOwes || parseFloat(amountOtherOwes) < 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/expenses`, {
        name: expenseName,
        cost: parseFloat(cost),
        who_paid: whoPaid,
        amount_other_owes: parseFloat(amountOtherOwes),
        is_credit: isCredit,
        date: expenseDate
      }, { headers: authHeaders });
      
      toast.success(isCredit ? 'Credit added!' : 'Expense added!');
      
      // Reset form
      setExpenseName('');
      setCost('');
      setIsCredit(false);
      setExpenseDate(localToday());
      
      fetchData();
    } catch (error) {
      toast.error('Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setEditName(expense.name);
    setEditCost(expense.cost.toString());
    setEditWhoPaid(expense.who_paid);
    setEditIsCredit(expense.is_credit);
    setEditDate(expense.date);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingExpense) return;
    if (!editName.trim() || !Number.isFinite(Number(editCost)) || Number(editCost) <= 0 || !editDate) {
      toast.error('Please enter a name, valid amount and date');
      return;
    }
    setEditSubmitting(true);
    
    try {
      await axios.put(`${API}/expenses/${editingExpense.id}`, {
        name: editName,
        cost: parseFloat(editCost),
        who_paid: editWhoPaid,
        amount_other_owes: parseFloat(editAmountOwes),
        is_credit: editIsCredit,
        date: editDate
      }, { headers: authHeaders });
      
      toast.success('Expense updated!');
      setEditDialogOpen(false);
      setEditingExpense(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update expense');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      await axios.delete(`${API}/expenses/${id}`, { headers: authHeaders });
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleSettleUp = async () => {
    if (!balance || balance.amount_owed === 0) return;
    
    const payer = balance.net_balance > 0 ? 'Gwen' : 'Chris';
    const receiver = balance.net_balance > 0 ? 'Chris' : 'Gwen';
    const confirmMsg = `Record that ${payer} paid ${receiver} $${balance.amount_owed.toFixed(2)} to settle up?`;
    if (!window.confirm(confirmMsg)) return;
    
    setSettlingUp(true);
    try {
      // A settlement offsets the debt by its full value, rather than half.
      await axios.post(`${API}/expenses`, {
        name: 'Settlement Payment',
        cost: balance.amount_owed,
        who_paid: payer,
        amount_other_owes: balance.amount_owed,
        is_credit: false,
        is_settlement: true,
        date: localToday()
      }, { headers: authHeaders });
      
      toast.success('Balance settled!');
      fetchData();
    } catch (error) {
      toast.error('Failed to settle up');
    } finally {
      setSettlingUp(false);
    }
  };

  // Group expenses by month
  const groupedExpenses = expenses.reduce((acc, expense) => {
    const date = calendarDate(expense.date);
    const monthKey = expense.date.slice(0, 7);
    const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    if (!acc[monthKey]) {
      acc[monthKey] = { name: monthName, expenses: [] };
    }
    acc[monthKey].expenses.push(expense);
    return acc;
  }, {});

  // Sort months in reverse chronological order
  const sortedMonths = Object.keys(groupedExpenses).sort().reverse();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="grain-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass-effect border-b border-border/30">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className="w-6 h-6 text-primary" />
            <h1 className="text-lg sm:text-xl font-bold text-primary">Expense Tracker</h1>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onLogout}
            className="text-muted-foreground hover:text-foreground"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 space-y-5 sm:space-y-8 max-w-4xl">
        {/* Balance Card */}
        {balance && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="balance-card" data-testid="balance-card">
              <CardContent className="p-4 sm:p-6 md:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <ArrowRightLeft className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground font-medium">Current Balance</span>
                </div>
                
                <div className="text-center py-4">
                  {balance.amount_owed === 0 ? (
                    <div className="space-y-2">
                      <span className="text-4xl md:text-5xl font-bold text-primary">All Settled!</span>
                      <p className="text-muted-foreground">No outstanding balance</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <span className={`text-4xl md:text-5xl font-bold font-mono ${balance.net_balance > 0 ? 'text-primary' : 'text-destructive'}`} data-testid="balance-amount">
                        ${balance.amount_owed.toFixed(2)}
                      </span>
                      <div className="flex items-center justify-center gap-2">
                        {balance.net_balance > 0 ? (
                          <TrendingUp className="w-5 h-5 text-primary" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-destructive" />
                        )}
                        <p className={`text-lg font-medium ${balance.net_balance > 0 ? 'text-primary' : 'text-destructive'}`} data-testid="balance-status">
                          {balance.who_owes}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {balance.amount_owed > 0 && (
                  <div className="mt-6 pt-6 border-t border-border/50">
                    <Button
                      onClick={handleSettleUp}
                      disabled={settlingUp}
                      className="w-full min-h-11 h-auto py-3 whitespace-normal rounded-full font-medium transition-all hover:shadow-lg active:scale-95 bg-accent text-accent-foreground hover:bg-accent/90"
                      data-testid="settle-up-button"
                    >
                      {settlingUp ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Settling...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <ArrowRightLeft className="w-4 h-4" />
                          Settle Up - {balance.net_balance > 0 ? 'Gwen' : 'Chris'} pays ${balance.amount_owed.toFixed(2)}
                        </span>
                      )}
                    </Button>
                  </div>
                )}
                
                {balance.amount_owed > 0 && (
                  <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-border/50">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Chris is owed</p>
                      <p className="text-xl font-mono font-semibold text-primary">${balance.gwen_owes_chris.toFixed(2)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Gwen is owed</p>
                      <p className="text-xl font-mono font-semibold text-primary">${balance.chris_owes_gwen.toFixed(2)}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Add Expense Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="form-card" data-testid="expense-form-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <Plus className="w-5 h-5" />
                {isCredit ? 'Add Credit' : 'Add Expense'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-muted-foreground" />
                    <Label htmlFor="is-credit" className="text-sm font-medium cursor-pointer">
                      This is a credit (external source)
                    </Label>
                  </div>
                  <Switch
                    id="is-credit"
                    checked={isCredit}
                    onCheckedChange={setIsCredit}
                    data-testid="credit-toggle"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="expense-name">{isCredit ? 'Credit Source' : 'Expense Name'}</Label>
                    <Input
                      id="expense-name"
                      placeholder={isCredit ? 'e.g., Tax refund' : 'e.g., Groceries'}
                      value={expenseName}
                      onChange={(e) => setExpenseName(e.target.value)}
                      className="h-11"
                      data-testid="expense-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="expense-date">Date</Label>
                    <Input
                      id="expense-date"
                      type="date"
                      value={expenseDate}
                      onChange={(e) => setExpenseDate(e.target.value)}
                      className="h-11"
                      data-testid="expense-date-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cost">{isCredit ? 'Credit Amount' : 'Total Cost'}</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="cost"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={cost}
                        onChange={(e) => setCost(e.target.value)}
                        className="h-11 pl-7 font-mono"
                        data-testid="cost-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="who-paid">{isCredit ? 'Received By' : 'Who Paid'}</Label>
                    <Select value={whoPaid} onValueChange={setWhoPaid}>
                      <SelectTrigger id="who-paid" className="h-11" data-testid="who-paid-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Chris">Chris</SelectItem>
                        <SelectItem value="Gwen">Gwen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="amount-owes">
                      {debtLabel(whoPaid, isCredit)}
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="amount-owes"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={amountOtherOwes}
                        readOnly
                        className="h-11 pl-7 font-mono"
                        data-testid="amount-owes-input"
                      />
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">Split equally. Half-shares round up to the nearest cent.</p>

                <Button 
                  type="submit" 
                  className="w-full h-11 rounded-full font-medium transition-all hover:shadow-lg active:scale-95"
                  disabled={submitting}
                  data-testid="add-expense-button"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Adding...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      {isCredit ? 'Add Credit' : 'Add Expense'}
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        <Card className="form-card" data-testid="monthly-report-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-xl">Monthly reports</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Export any month as a CSV for Excel or other spreadsheet apps, with expenses, credits, settlements, and opening and closing balances.</p>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="space-y-2 flex-1 min-w-0">
                <Label htmlFor="report-month">Report month</Label>
                <Input id="report-month" type="month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} className="h-11" data-testid="report-month-input" />
              </div>
              <Button className="h-11" onClick={handleExport} disabled={exporting || !reportMonth} data-testid="export-report-button">
                <Download className="w-4 h-4 mr-2" />{exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Expense History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card className="form-card" data-testid="expense-history-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Expense History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sortedMonths.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No expenses yet. Add your first expense above!</p>
                </div>
              ) : (
                <div>
                  <div className="space-y-8">
                    <AnimatePresence>
                      {sortedMonths.map((monthKey) => (
                        <motion.div
                          key={monthKey}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <h3 className="month-header" data-testid={`month-${monthKey}`}>
                            {groupedExpenses[monthKey].name}
                          </h3>
                          <div className="space-y-3">
                            <AnimatePresence>
                              {groupedExpenses[monthKey].expenses
                                .sort((a, b) => new Date(b.date) - new Date(a.date))
                                .map((expense) => (
                                  <motion.div
                                    key={expense.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    className="expense-item"
                                    data-testid={`expense-item-${expense.id}`}
                                  >
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                          <span className="font-medium text-foreground break-words min-w-0">
                                            {expense.name}
                                          </span>
                                          {expense.is_credit && (
                                            <span className="credit-badge">Credit</span>
                                          )}
                                          {expense.is_settlement && <span className="credit-badge">Settlement</span>}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                          <span>
                                            {calendarDate(expense.date).toLocaleDateString('en-US', { 
                                              month: 'short', 
                                              day: 'numeric' 
                                            })}
                                          </span>
                                          <span>
                                            {expense.is_credit ? 'Received by' : 'Paid by'} {expense.who_paid}
                                          </span>
                                        </div>
                                      </div>
                                      
                                      <div className="flex items-center justify-between sm:justify-end gap-3">
                                        <div className="text-left sm:text-right">
                                          <p className="font-mono font-semibold text-foreground">
                                            ${expense.cost.toFixed(2)}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {expense.is_settlement ? 'Payment applied' : debtLabel(expense.who_paid, expense.is_credit)} ${expense.amount_other_owes.toFixed(2)}
                                          </p>
                                        </div>
                                        
                                        <div className="flex items-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-11 w-11 text-muted-foreground hover:text-foreground"
                                            aria-label={`Edit ${expense.name}`}
                                            onClick={() => handleEdit(expense)}
                                            data-testid={`edit-expense-${expense.id}`}
                                          >
                                            <Pencil className="w-4 h-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-11 w-11 text-muted-foreground hover:text-destructive"
                                            aria-label={`Delete ${expense.name}`}
                                            onClick={() => handleDelete(expense.id)}
                                            data-testid={`delete-expense-${expense.id}`}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-h-[90dvh] overflow-y-auto rounded-lg sm:max-w-md" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editingExpense?.is_settlement ? 'Settlement' : editIsCredit ? 'Credit' : 'Expense'}</DialogTitle>
            <DialogDescription>Update the details below and save your changes.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!editingExpense?.is_settlement && <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <Label htmlFor="edit-is-credit" className="text-sm font-medium cursor-pointer">
                This is a credit
              </Label>
              <Switch
                id="edit-is-credit"
                checked={editIsCredit}
                onCheckedChange={setEditIsCredit}
                data-testid="edit-credit-toggle"
              />
            </div>}
            
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                data-testid="edit-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                data-testid="edit-date-input"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-cost">Total Cost</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="edit-cost"
                    type="number"
                    step="0.01"
                    value={editCost}
                    onChange={(e) => setEditCost(e.target.value)}
                    className="pl-7 font-mono"
                    data-testid="edit-cost-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-who-paid">{editIsCredit ? 'Received By' : 'Who Paid'}</Label>
                <Select value={editWhoPaid} onValueChange={setEditWhoPaid}>
                  <SelectTrigger id="edit-who-paid" data-testid="edit-who-paid-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Chris">Chris</SelectItem>
                    <SelectItem value="Gwen">Gwen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-amount-owes">{editingExpense?.is_settlement ? 'Payment applied' : debtLabel(editWhoPaid, editIsCredit)}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="edit-amount-owes"
                  type="number"
                  step="0.01"
                  value={editAmountOwes}
                  readOnly
                  className="pl-7 font-mono"
                  data-testid="edit-amount-owes-input"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="edit-cancel-button">
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={editSubmitting} data-testid="edit-save-button">
              {editSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
